import React, { useMemo } from 'react';
import { SearchDropdown } from '../../../../../components';
import { RoofType, RoofCondition, ReroofStatus, BuildingHeight } from '../../../../../types';
import { REROOF_ENABLED_ROOF_TYPES } from '../../../../../constants/app.constants';

interface RoofConfigurationPropsInterface {
  roofType?: string | RoofType;
  roofCondition?: string | RoofCondition;
  reroofStatus?: string | ReroofStatus;
  buildingHeight?: string | BuildingHeight;
  onRoofTypeChange: (roofType?: RoofType) => void;
  onRoofConditionChange: (roofCondition?: RoofCondition) => void;
  onReroofStatusChange: (reroofStatus?: ReroofStatus) => void;
  onBuildingHeightChange: (buildingHeight?: BuildingHeight) => void;
  roofTypes: RoofType[];
  roofConditions: RoofCondition[];
  reroofStatuses: ReroofStatus[];
  buildingHeights: BuildingHeight[];
  isLoading: boolean;
  isEnabled: boolean;
}

export const RoofConfigurationProps: React.FC<RoofConfigurationPropsInterface> = ({
  roofType,
  roofCondition,
  reroofStatus,
  buildingHeight,
  onRoofTypeChange,
  onRoofConditionChange,
  onReroofStatusChange,
  onBuildingHeightChange,
  roofTypes,
  roofConditions,
  reroofStatuses,
  buildingHeights,
  isLoading,
  isEnabled
}) => {
  // Extract string values from props (handle both string and object cases)
  const roofTypeValue = useMemo(() => {
    if (!roofType) return undefined;
    return typeof roofType === 'string' ? roofType : roofType.type;
  }, [roofType]);

  const roofConditionValue = useMemo(() => {
    if (!roofCondition) return undefined;
    return typeof roofCondition === 'string' ? roofCondition : roofCondition.value;
  }, [roofCondition]);

  const reroofStatusValue = useMemo(() => {
    if (!reroofStatus) return undefined;
    return typeof reroofStatus === 'string' ? reroofStatus : reroofStatus.value;
  }, [reroofStatus]);

  const buildingHeightValue = useMemo(() => {
    if (!buildingHeight) return undefined;
    return typeof buildingHeight === 'string' ? buildingHeight : buildingHeight.value;
  }, [buildingHeight]);

  // Find full objects from extracted string values
  const selectedRoofType = useMemo(() => {
    if (!roofTypeValue) return undefined;
    return roofTypes.find(rt => rt.type === roofTypeValue);
  }, [roofTypeValue, roofTypes]);

  const selectedRoofCondition = useMemo(() => {
    if (!roofConditionValue) return undefined;
    return roofConditions.find(rc => rc.value === roofConditionValue);
  }, [roofConditionValue, roofConditions]);

  const selectedReroofStatus = useMemo(() => {
    if (!reroofStatusValue) return undefined;
    return reroofStatuses.find(rs => rs.value === reroofStatusValue);
  }, [reroofStatusValue, reroofStatuses]);

  const selectedBuildingHeight = useMemo(() => {
    if (!buildingHeightValue) return undefined;
    return buildingHeights.find(bh => bh.value === buildingHeightValue);
  }, [buildingHeightValue, buildingHeights]);

  const isReroofEnabled = useMemo(() => {
    if (!roofTypeValue) return false;
    return REROOF_ENABLED_ROOF_TYPES.includes(roofTypeValue as any);
  }, [roofTypeValue]);

  // Convert data to SearchDropdown option format
  const roofTypeOptions = useMemo(() => {
    return roofTypes.map(item => ({ id: item.id, label: item.type }));
  }, [roofTypes]);

  const roofConditionOptions = useMemo(() => {
    return roofConditions.map(item => ({ id: item.id, label: item.value }));
  }, [roofConditions]);

  const reroofStatusOptions = useMemo(() => {
    return reroofStatuses.map(item => ({ id: item.id, label: item.value }));
  }, [reroofStatuses]);

  const buildingHeightOptions = useMemo(() => {
    return buildingHeights.map(item => ({ id: item.id, label: item.value }));
  }, [buildingHeights]);

  const handleRoofTypeSelect = (option: { id: string; label: string } | null) => {
    const selectedRoofType = option ? roofTypes.find(rt => rt.id === option.id) : undefined;
    onRoofTypeChange(selectedRoofType);
    
    // Clear reroof status if the new roof type doesn't support it
    if (selectedRoofType && !REROOF_ENABLED_ROOF_TYPES.includes(selectedRoofType.type as any)) {
      onReroofStatusChange(undefined);
    }
  };

  const handleRoofConditionSelect = (option: { id: string; label: string } | null) => {
    const selectedRoofCondition = option ? roofConditions.find(rc => rc.id === option.id) : undefined;
    onRoofConditionChange(selectedRoofCondition);
  };

  const handleReroofStatusSelect = (option: { id: string; label: string } | null) => {
    const selectedReroofStatus = option ? reroofStatuses.find(rs => rs.id === option.id) : undefined;
    onReroofStatusChange(selectedReroofStatus);
  };

  const handleBuildingHeightSelect = (option: { id: string; label: string } | null) => {
    const selectedBuildingHeight = option ? buildingHeights.find(bh => bh.id === option.id) : undefined;
    onBuildingHeightChange(selectedBuildingHeight);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Roof Type */}
      <div>
        <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1 block">
          Roof Type
        </label>
        <SearchDropdown
          value={selectedRoofType ? { id: selectedRoofType.id, label: selectedRoofType.type } : null}
          onSelect={handleRoofTypeSelect}
          options={roofTypeOptions}
          placeholder="Select roof type..."
          className="w-full"
          inputClassName="h-8 text-xs 3xl:text-sm"
          disabled={!isEnabled || isLoading}
        />
      </div>

      {/* Roof Condition */}
      <div>
        <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1 block">
          Roof Condition
        </label>
        <SearchDropdown
          value={selectedRoofCondition ? { id: selectedRoofCondition.id, label: selectedRoofCondition.value } : null}
          onSelect={handleRoofConditionSelect}
          options={roofConditionOptions}
          placeholder="Select roof condition..."
          className="w-full"
          inputClassName="h-8 text-xs 3xl:text-sm"
          disabled={!isEnabled || isLoading}
        />
      </div>

      {/* Reroof Options */}
      <div>
        <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
          Reroof Status
          {!isReroofEnabled && (
            <span className="relative group cursor-help">
              <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[10px] 3xl:text-xs font-medium text-white bg-slate-800 rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                Available only for specific roof types
              </span>
            </span>
          )}
        </label>
        <SearchDropdown
          value={selectedReroofStatus ? { id: selectedReroofStatus.id, label: selectedReroofStatus.value } : null}
          onSelect={handleReroofStatusSelect}
          options={reroofStatusOptions}
          placeholder={isReroofEnabled ? "Select reroof status..." : "Select roof type first"}
          disabled={!isEnabled || isLoading || !isReroofEnabled}
          className="w-full"
          inputClassName="h-8 text-xs 3xl:text-sm"
        />
      </div>

      {/* Building Height */}
      <div>
        <label className="text-xs 3xl:text-sm font-semibold text-gray-700 mb-1 block">
          Building Height
        </label>
        <SearchDropdown
          value={selectedBuildingHeight ? { id: selectedBuildingHeight.id, label: selectedBuildingHeight.value } : null}
          onSelect={handleBuildingHeightSelect}
          options={buildingHeightOptions}
          placeholder="Select building height..."
          className="w-full"
          inputClassName="h-8 text-xs 3xl:text-sm"
          disabled={!isEnabled || isLoading}
        />
      </div>
    </div>
  );
};